/**
 * Preço do plano de equipe (seção 4.1 do plano). Mesmas funções puras de
 * `api/src/stripe/team-pricing.ts`, tudo em centavos: o app calcula na hora,
 * sem chamar a API a cada toque, e o backend recalcula no checkout — o total
 * daqui nunca é a verdade.
 *
 *   preço = taxa da organização + profissionais × assento + ⌈clientes ÷ bloco⌉ × valor do bloco
 */

export interface TeamPricingVersion {
  id: string;
  orgFeeCents: number;
  seatCents: number;
  blockCents: number;
  blockSize: number;
  minProfessionals: number;
  maxProfessionals: number;
  maxClients: number;
}

/** Degrau da escada Apple: produto de preço fixo no mesmo grupo de assinatura. */
export interface AppleLadderStep {
  productId: string;
  priceCents: number;
}

/** `GET /stripe/plans/team`. */
export interface TeamPricing {
  version: TeamPricingVersion;
  appleLadder: AppleLadderStep[];
}

export interface TeamSelection {
  professionals: number;
  clients: number;
}

export interface TeamQuote {
  totalCents: number;
  feeCents: number;
  seatsCents: number;
  blocks: number;
  blocksCents: number;
  /** Clientes que os blocos cobrem (sempre múltiplo do tamanho do bloco). */
  clientsCap: number;
}

/**
 * Espelha a versão 1 semeada na API. Só a landing usa, quando
 * `/stripe/plans/team` não responde — como o `FALLBACK_PLANS` dos planos solo.
 */
export const FALLBACK_TEAM_PRICING: TeamPricing = {
  version: {
    id: 'fallback-v1',
    orgFeeCents: 2990,
    seatCents: 1990,
    blockCents: 750,
    blockSize: 50,
    minProfessionals: 2,
    maxProfessionals: 20,
    maxClients: 5000,
  },
  appleLadder: [],
};

export function blocksFor(version: TeamPricingVersion, clients: number): number {
  return Math.max(1, Math.ceil(Math.max(0, clients) / version.blockSize));
}

export function quoteTeamPlan(version: TeamPricingVersion, selection: TeamSelection): TeamQuote {
  const blocks = blocksFor(version, selection.clients);
  const feeCents = version.orgFeeCents;
  const seatsCents = selection.professionals * version.seatCents;
  const blocksCents = blocks * version.blockCents;
  return {
    totalCents: feeCents + seatsCents + blocksCents,
    feeCents,
    seatsCents,
    blocks,
    blocksCents,
    clientsCap: blocks * version.blockSize,
  };
}

/**
 * Quantos clientes um orçamento fixo compra com N profissionais — a conta do
 * degrau Apple. `null` quando nem um bloco cabe.
 */
export function capacityForBudget(
  version: TeamPricingVersion,
  priceCents: number,
  professionals: number,
): number | null {
  const left = priceCents - version.orgFeeCents - professionals * version.seatCents;
  if (left < version.blockCents) return null;
  const blocks = Math.floor(left / version.blockCents);
  return Math.min(blocks * version.blockSize, version.maxClients);
}

/** O menor degrau que cobre o total; `null` acima do maior. */
export function ladderStepFor(
  ladder: AppleLadderStep[],
  totalCents: number,
): AppleLadderStep | null {
  return (
    [...ladder]
      .sort((a, b) => a.priceCents - b.priceCents)
      .find((step) => step.priceCents >= totalCents) ?? null
  );
}

/** Arredonda para o bloco de cima, dentro dos limites da versão. */
export function roundClients(version: TeamPricingVersion, clients: number): number {
  const rounded = blocksFor(version, clients) * version.blockSize;
  return Math.min(Math.max(rounded, version.blockSize), version.maxClients);
}

export function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
